import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "../../assets/scss/_exitmodal.scss";
import "../../assets/scss/_reject-modal.scss";
import Notes from "../Forms/Notes";
import { vhKitRejectionReasons } from "../Forms/fields";
import closeIcon from "../../assets/icons/Close.png";
import infoIcon from "../../assets/icons/Information-fill.svg";
import chevronDownIcon from "../../assets/icons/Chevron-down.svg";
import checkIcon from "../../assets/icons/Check-dark.svg";

const KIT_REASONS = vhKitRejectionReasons;
const THUMB_SIZE = 74;

const RejectKitModal = ({
  isOpen,
  onClose,
  onReject,
  details,
  title = "Reject test kit?",
  subtitle = "Are you sure you want to reject the following test kit?",
  reasonLabel = "Select reason for kit rejection",
  reasons = KIT_REASONS,
  infoText = "Please also follow your organisation’s reporting processes for any kit incidents.",
  confirmLabel = "Confirm & reject",
  className = "",
}) => {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const bodyRef = useRef(null);
  const [thumbTop, setThumbTop] = useState(0);
  const [scrollable, setScrollable] = useState(false);

  const getOptionValue = (r) =>
    typeof r === "object" && r !== null ? r.value : r;
  const getOptionLabel = (r) =>
    typeof r === "object" && r !== null ? r.label : r;
  const isOptionDisabled = (r) =>
    Boolean(typeof r === "object" && r !== null && r.disabled);

  const selectedOption = reasons.find(
    (r) => getOptionValue(r) === reason
  );
  const selectedLabel = selectedOption
    ? getOptionLabel(selectedOption)
    : "Select a reason";

  const updateThumb = () => {
    const el = bodyRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    setScrollable(max > 0);
    setThumbTop(
      max > 0 ? (el.scrollTop / max) * (el.clientHeight - THUMB_SIZE) : 0,
    );
  };

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setNotes("");
      setOpen(false);
    }
  }, [isOpen]);

    useEffect(() => {
    if (!isOpen) return;
    if (open) {
      bodyRef.current?.scrollTo?.({
        top: bodyRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
    updateThumb();
  }, [isOpen, open, details]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (!dropdownRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  if (!isOpen) return null;

  return (
    <div className="exit-consultation-overlay">
      <div
        aria-modal="true"
        className={`exit-consultation-modal ${className}`.trim()}
        role="dialog"
      >
        <div className="exit-consultation-modal__header">
          <h2 className="exit-consultation-modal__title">{title}</h2>
          <button
            aria-label="Close"
            className="exit-consultation-modal__close"
            onClick={onClose}
            type="button"
          >
            <img alt="" height="24" src={closeIcon} width="24" />
          </button>
        </div>

        <div className="reject-modal__scroll">
        <div
          className="exit-consultation-modal__body reject-modal__body"
          onScroll={updateThumb}
          ref={bodyRef}
        >
          <p className="exit-consultation-modal__subtitle">
            {subtitle}
          </p>

          <dl className="reject-modal__details">
            {details.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt>{label}</dt>
                <dd className={label === "Consultation ID:" ? "boots-bold" : ""}>
                  {value || "-"}
                </dd>
              </React.Fragment>
            ))}
          </dl>

          <div className="reject-modal__info">
            <img alt="" src={infoIcon} />
            <span>{infoText}</span>
          </div>

          <div className="exit-consultation-modal__field" ref={dropdownRef}>
            <label className="exit-consultation-modal__label">
              {reasonLabel}
            </label>
            <div className="exit-consultation-modal__dropdown-wrapper">
              <button
                aria-expanded={open}
                className={`exit-consultation-modal__dropdown${open ? " exit-consultation-modal__dropdown--open" : ""}`}
                onClick={() => setOpen((prev) => !prev)}
                type="button"
              >
                <span>{selectedLabel}</span>
                <span
                  className={`exit-consultation-modal__chevron${open ? " exit-consultation-modal__chevron--open" : ""}`}
                >
                  <img alt="" src={chevronDownIcon} />
                </span>
              </button>
              {open && (
                <ul
                  className="exit-consultation-modal__dropdown-panel"
                  role="listbox"
                >
                  {reasons.map((r) => {
                    const value = getOptionValue(r);
                    const label = getOptionLabel(r);
                    const disabled = isOptionDisabled(r);
                    const isSelected = reason === value;
                    return (
                      <li
                        aria-disabled={disabled || undefined}
                        aria-selected={isSelected}
                        className={`exit-consultation-modal__dropdown-option${
                          disabled
                            ? " exit-consultation-modal__dropdown-option--disabled"
                            : ""
                        }${
                          isSelected
                            ? " exit-consultation-modal__dropdown-option--selected"
                            : ""
                        }`}
                        key={value}
                        onClick={() => {
                          if (disabled) return;
                          setReason(value);
                          setOpen(false);
                        }}
                        role="option"
                      >
                        <span>{label}</span>
                        {isSelected && <img alt="" src={checkIcon} />}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="exit-consultation-modal__notes-wrapper">
            <Notes label="Rejection notes" mandatory onChange={setNotes} value={notes} />
          </div>
        </div>
        {scrollable && (
          <div className="reject-modal__scrollbar">
            <div
              className="reject-modal__scrollbar-thumb"
              style={{
                height: THUMB_SIZE,
                transform: `translateY(${thumbTop}px)`,
              }}
            />
          </div>
        )}
        </div>

        <div className="exit-consultation-modal__footer">
          <button
            className="exit-consultation-modal__cancel"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="exit-consultation-modal__confirm"
            disabled={!reason || !notes.trim()}
            onClick={() => onReject({ reason, notes })}
            type="button"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

RejectKitModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  details: PropTypes.arrayOf(PropTypes.array),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  reasonLabel: PropTypes.string,
  reasons: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
      }),
    ]),
  ),
  infoText: PropTypes.string,
  confirmLabel: PropTypes.string,
  className: PropTypes.string,
};

RejectKitModal.defaultProps = {
  details: [],
  title: "Reject test kit?",
  subtitle: "Are you sure you want to reject the following test kit?",
  reasonLabel: "Select reason for kit rejection",
  reasons: KIT_REASONS,
  infoText:
    "Please also follow your organisation’s reporting processes for any kit incidents.",
  confirmLabel: "Confirm & reject",
};

export default RejectKitModal;